// frontend/src/services/api.js
import axios from "axios";
import { msalInstance } from "../auth/msal";
import { loginRequest } from "../msalConfig";
import { maskPath } from "./urlMask";

const API = axios.create({
  // Use same-origin API by default (backend + frontend served on one port)
  // In most environments this should just be "/api".
  baseURL: process.env.REACT_APP_API_BASE_URL || "/api",
  withCredentials: true, // Enable session cookies
});

// Interceptor to attach Authorization header using msal
API.interceptors.request.use(
  async (config) => {
    // Identify user for per-user blob containers (hoisted for catch access)
    let userIdHeader = null;
    try {
      // Try Azure AD authentication
      let accounts = [];
      try {
        accounts = msalInstance.getAllAccounts();
        if (accounts.length === 0) {
          // not logged in with either method
          if (userIdHeader) {
            config.headers = config.headers || {};
            config.headers['X-User-Id'] = userIdHeader;
          } else {
            // Fallback for session-based auth
            const storedUserId = localStorage.getItem('user_id');
            if (storedUserId) {
              config.headers = config.headers || {};
              config.headers['X-User-Id'] = storedUserId;
            }
          }
          return config;
        }
      } catch (error) {
        // MSAL not initialized yet, skip Azure AD auth
        if (userIdHeader) {
          config.headers = config.headers || {};
          config.headers['X-User-Id'] = userIdHeader;
        } else {
          // Fallback for session-based auth (refresh/direct access)
          const storedUserId = localStorage.getItem('user_id');
          if (storedUserId) {
            config.headers = config.headers || {};
            config.headers['X-User-Id'] = storedUserId;
          }
        }
        return config;
      }

      const silentRequest = {
        ...loginRequest,
        account: accounts[0],
      };
      const resp = await msalInstance.acquireTokenSilent(silentRequest);
      const accessToken = resp.accessToken;
      config.headers = config.headers || {};
      config.headers["Authorization"] = `Bearer ${accessToken}`;
      // Prefer username; fallback to homeAccountId
      const acct = accounts[0];
      const azureUserId = acct?.username || acct?.homeAccountId || null;
      if (azureUserId) {
        config.headers['X-User-Id'] = azureUserId;
      } else if (userIdHeader) {
        config.headers['X-User-Id'] = userIdHeader;
      }
    } catch (err) {
      // fallback: try interactive (will open popup/redirect) only for Azure
      try {
        const resp = await msalInstance.acquireTokenPopup(loginRequest);
        config.headers = config.headers || {};
        config.headers["Authorization"] = `Bearer ${resp.accessToken}`;
        const accounts = msalInstance.getAllAccounts();
        const acct = accounts && accounts[0];
        const azureUserId = acct?.username || acct?.homeAccountId || null;
        if (azureUserId) {
          config.headers['X-User-Id'] = azureUserId;
        }
      } catch (e) {
        console.warn("Could not acquire token silently or via popup", e);
        // still send any derived guest header
        if (userIdHeader) {
          config.headers = config.headers || {};
          config.headers['X-User-Id'] = userIdHeader;
        }
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// URL masking interceptor — rewrites the readable relative path (+query) into an
// opaque "/__/<token>" so the Network tab never shows route names like /admin/users.
// Runs synchronously after the auth interceptor above (axios runs request
// interceptors last-registered-first, but order is irrelevant here since this only
// touches config.url / config.params, not headers). The backend ASGI middleware
// (backend/app/middleware/url_mask.py) decodes it back before auth/routing.
API.interceptors.request.use(
  (config) => {
    if (!config.url || config.url.startsWith("/__/")) return config;
    let pathAndQuery = config.url;
    if (config.params && typeof config.params === "object") {
      const usp = new URLSearchParams();
      Object.entries(config.params).forEach(([k, v]) => {
        if (v !== undefined && v !== null) usp.append(k, v);
      });
      const qs = usp.toString();
      if (qs) pathAndQuery += (pathAndQuery.includes("?") ? "&" : "?") + qs;
      delete config.params; // critical: stop axios re-appending a readable query string
    }
    config.url = maskPath(pathAndQuery);
    return config;
  },
  (error) => Promise.reject(error)
);

// ------------------ Named API helper exports ------------------

// Upload resume (single file or zip). Expects FormData with "resume" field.
// Pass { signal } to support AbortController-based cancel.
export const uploadResume = (formData, options = {}) =>
  API.post("/upload", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    signal: options.signal,
  });

// Get insights (skill frequencies, etc.)
export const getInsights = () => API.get("/insights");

// Get ranking status (progress)
export const getRankingStatus = () => API.get("/rank/status");

// Get existing ranking results.
// Request the full result set (page_size large) so the summary stats and tier
// counts are computed over ALL candidates. Display paging is handled client-side.
export const getRankingResults = (sessionId = null) => {
  const params = new URLSearchParams({ page_size: "1000" });
  if (sessionId) params.set("session_id", sessionId);
  return API.get(`/rank/results?${params.toString()}`);
};

// Rank resumes (job_description string with optional fit threshold and mode)
// rankingMode: "ai" (full pipeline) or "economy" (cheap local scoring)
export const rankResumes = (jobDescription, fitThreshold = 30, rankingMode = "ai") =>
  API.post("/rank", { job_description: jobDescription, fit_threshold: fitThreshold, ranking_mode: rankingMode });

// Rank resumes from a job description document
export const rankResumesFromFile = (formData) =>
  API.post("/rank-from-file", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

// Cancel / Pause / Resume an in-progress ranking job
export const cancelRanking = () => API.post("/rank/cancel");
export const pauseRanking = () => API.post("/rank/pause");
export const resumeRanking = () => API.post("/rank/resume");

// Download report securely with auth header.
// sessionId scopes the report to the session being viewed — without it the
// backend falls back to the *active* session, so the file you get can describe a
// different set of candidates than the ones on screen.
export const downloadReport = async (type, sessionId = null) => {
  const qs = sessionId ? `?${new URLSearchParams({ session_id: sessionId })}` : "";
  const res = await API.get(`/download/${type}${qs}`, { responseType: "blob" });
  const blob = new Blob([res.data], {
    type: res.headers["content-type"] || "application/octet-stream",
  });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  // Map logical type to proper file extension
  const ext = type === 'excel' ? 'xlsx' : (type === 'csv' ? 'csv' : (type === 'pdf' ? 'pdf' : type));
  a.download = `ranked_resumes.${ext}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// ------------------ Sessions API ------------------

// List all sessions for the current user. Soft-deleted (archived) sessions are omitted:
// once a user deletes a session it must disappear from every list (dashboard + sessions page).
export const listSessions = () => API.get("/sessions/list");

// Get the currently active session ID
export const getCurrentSession = () => API.get("/sessions/current");

// Create a new session
export const createSession = (name) => API.post("/sessions/create", { name });

// Set a session as active
export const setActiveSession = (sessionId) => API.post(`/sessions/${sessionId}/set-active`);

// Rename a session
export const renameSession = (sessionId, name) => API.put(`/sessions/${sessionId}/name`, { name });

// Delete a session
export const deleteSession = (sessionId) => API.delete(`/sessions/${sessionId}`);

// Get file counts for sessions (batch)
export const getSessionFileCounts = () => API.get("/sessions/batch/file-counts");

// Check if session has results or files
export const checkSessionHasResults = (sessionId) => API.get(`/sessions/${sessionId}/has-results`);

// Download fit resumes for a session (defaults to the active one when omitted)
export const downloadFitResumes = async (sessionId = null) => {
  const qs = sessionId ? `?${new URLSearchParams({ session_id: sessionId })}` : "";
  const res = await API.get(`/download-fit-resumes${qs}`, { responseType: "blob" });
  const blob = new Blob([res.data], { type: "application/zip" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `fit_resumes_${Date.now()}.zip`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// ------------------ Resume Library ------------------
// Central, cross-session index of every resume in the user's blob storage.

// Trigger a browser download from a blob response, reused by both library downloads.
const saveBlob = (res, fallbackName) => {
  const blob = new Blob([res.data], {
    type: res.headers["content-type"] || "application/octet-stream",
  });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  // Prefer the filename the server set, so a .docx never lands as a .pdf.
  const disposition = res.headers["content-disposition"] || "";
  const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  a.download = match ? decodeURIComponent(match[1]) : fallbackName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// List library resumes. Server-side search/filter/sort/pagination — params are passed
// straight through, so undefined values are dropped by axios rather than sent as "".
export const listLibraryResumes = (params = {}) =>
  API.get("/library/resumes", {
    params: Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
    ),
  });

// Download one resume.
export const downloadLibraryResume = async (resumeId, filename = "resume") => {
  const res = await API.get(`/library/resumes/${resumeId}/download`, { responseType: "blob" });
  saveBlob(res, filename);
};

// Download a selection as a single ZIP.
export const downloadLibraryZip = async (resumeIds) => {
  const res = await API.post(
    "/library/resumes/download-zip",
    { resume_ids: resumeIds },
    { responseType: "blob" }
  );
  saveBlob(res, `resumes_${Date.now()}.zip`);
};

// Short-lived signed URL for in-place preview (PDF/TXT only).
export const getLibraryPreviewUrl = (resumeId) => API.get(`/library/resumes/${resumeId}/preview-url`);

// Extracted text, for formats a browser cannot render (chiefly .docx).
export const getLibraryResumeText = (resumeId) => API.get(`/library/resumes/${resumeId}/text`);

// Move resumes to the bin. Recoverable — see restoreLibraryResumes. Also tells connected
// mailboxes to stop re-importing them, which unblockLibraryResume reverses.
export const deleteLibraryResumes = (resumeIds) => API.post("/library/resumes/delete", { resume_ids: resumeIds });

// Put binned resumes back in the drive and screening state they came from.
export const restoreLibraryResumes = (resumeIds) => API.post("/library/resumes/restore", { resume_ids: resumeIds });

// Destroy binned resumes now instead of waiting out the window. Irreversible.
export const purgeLibraryResumes = (resumeIds) => API.post("/library/resumes/purge", { resume_ids: resumeIds });

// Resumes the user deleted, which mailbox ingestion is refusing to re-add.
export const listBlockedResumes = (connectionId) =>
  API.get("/library/blocked", { params: connectionId ? { connection_id: connectionId } : {} });

// Lift one import block, so the next sync brings that resume back in.
export const unblockResume = (blockedId) => API.delete(`/library/blocked/${blockedId}`);

// ------------------ Connected Mailboxes ------------------
// Automatic resume ingestion from a mailbox (hr@company.com and friends).
// Every one of these is scoped to the signed-in user server-side; there is no
// org-wide variant, so nothing here takes a scope parameter.

// Which connection methods this deployment offers, and whether encryption is ready.
export const getMailboxProviders = () => API.get("/mailbox/providers");

// Suggest provider + IMAP settings for an address. A guess, always user-editable.
export const detectMailboxProvider = (email) => API.get("/mailbox/detect", { params: { email } });

// Verify credentials and return the folder list. Persists nothing.
export const testMailboxConnection = (payload) => API.post("/mailbox/test-connection", payload);

// Dry run: what WOULD be imported. Writes nothing — no drives, no resumes, no cursor.
export const previewMailbox = (payload) => API.post("/mailbox/preview", payload);

export const listMailboxConnections = () => API.get("/mailbox/connections");

// Creates one drive per selected folder and kicks off the first sync immediately.
export const createMailboxConnection = (payload) => API.post("/mailbox/connections", payload);

export const updateMailboxConnection = (id, payload) => API.patch(`/mailbox/connections/${id}`, payload);

// Revokes the stored credential. Resumes already imported are kept.
export const deleteMailboxConnection = (id) => API.delete(`/mailbox/connections/${id}`);

// Returns 202 immediately; poll listMailboxRuns for progress.
export const syncMailboxNow = (id) => API.post(`/mailbox/connections/${id}/sync`);

export const listMailboxRuns = (id, limit = 20) => API.get(`/mailbox/connections/${id}/runs`, { params: { limit } });

// Drives available as a target for a watched folder. Its own endpoint rather than
// /sessions/list, which still trusts a client-supplied X-User-Id header.
export const listMailboxDrives = () => API.get("/mailbox/drives");

// How many resumes a drive change would move — backs the confirm dialog's warning.
export const previewMailboxDriveChange = (connectionId, mappingId) =>
  API.get(`/mailbox/connections/${connectionId}/folders/${mappingId}/drive`);

// Point a watched folder at a different drive, taking its resumes with it.
// Pass session_id for an existing drive, or session_name to create a new one.
export const changeMailboxDrive = (connectionId, mappingId, payload) =>
  API.post(`/mailbox/connections/${connectionId}/folders/${mappingId}/drive`, payload);

// Processed messages with per-attachment verdicts — the "why didn't that candidate
// show up?" view, mirroring the upload page's rejected-files panel.
export const listMailboxMessages = (id, limit = 50) => API.get(`/mailbox/connections/${id}/messages`, { params: { limit } });

// Consent redirect for the OAuth providers (Microsoft; Gmail when enabled).
export const startMailboxOAuth = (provider, email) =>
  API.get(`/mailbox/oauth/${provider}/start`, { params: email ? { email } : {} });

// ------------------ Authentication ------------------

// Check if user is authenticated with Azure AD or session-based auth
export const isAuthenticated = async () => {
  // Check for Azure AD authentication first
  try {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      return { type: 'azure', account: accounts[0] };
    }
  } catch (error) {
    // MSAL not initialized yet, ignore Azure AD auth
  }

  // Check for session-based authentication
  try {
    const response = await API.get('/auth/status', {
      withCredentials: true,
    });
    if (response.data && response.data.authenticated) {
      // Update local storage to match the server session (prevents stale X-User-Id header)
      const user = response.data.user;
      const userId = user.email || user.id;
      if (userId) {
        const currentStored = localStorage.getItem('user_id');
        if (currentStored !== userId) {
          console.log('🔄 updating localStorage user_id from session:', userId);
          localStorage.setItem('user_id', userId);
        }
      }
      return { type: 'session', user: response.data.user };
    }
  } catch (error) {
    // Session check failed, not authenticated
  }

  return null;
};

// ------------------ Session-Based Authentication ------------------

// Register a new user with email/password
export const registerUser = async (email, password, fullName) => {
  const response = await API.post('/auth/register', {
    email,
    password,
    full_name: fullName,
  });
  return response.data;
};

// Login with email/password (session-based)
// `rememberMe` controls cookie lifetime: true (default) persists across browser
// restarts; false makes it a session cookie that clears on close.
export const loginWithPassword = async (email, password, rememberMe = true) => {
  const formData = new URLSearchParams();
  formData.append('username', email); // OAuth2 form uses 'username' field
  formData.append('password', password);
  formData.append('remember_me', rememberMe ? 'true' : 'false');

  const response = await API.post('/auth/session-login', formData, {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    withCredentials: true, // Important for session cookies
  });

  // Store user email in localStorage for session-based auth
  // This is used by createAuthHeadersAsync to include X-User-Id header
  if (response.data && response.data.user) {
    localStorage.setItem('user_id', response.data.user.email || response.data.user.id);
    console.log('💾 Stored user_id in localStorage:', response.data.user.email || response.data.user.id);
  }

  return response.data;
};

// Change the signed-in user's password (requires current password).
export const changePassword = async (currentPassword, newPassword) => {
  const response = await API.post(
    '/auth/change-password',
    { current_password: currentPassword, new_password: newPassword },
    { withCredentials: true },
  );
  return response.data;
};

// Logout from session-based auth
export const logoutSession = async () => {
  const response = await API.post('/auth/logout', {}, {
    withCredentials: true,
  });

  // Clear user_id from localStorage
  localStorage.removeItem('user_id');
  console.log('🚪 Cleared user_id from localStorage on logout');

  return response.data;
};

// Get current authenticated user info
export const getCurrentUser = async () => {
  try {
    const response = await API.get('/auth/me', {
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    return null;
  }
};

// Get authentication status
export const getAuthStatus = async () => {
  try {
    const response = await API.get('/auth/status', {
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    return { authenticated: false, user: null };
  }
};

// ------------------ Password Reset ------------------

// Request password reset email
export const forgotPassword = async (email) => {
  const response = await API.post('/auth/forgot-password', { email });
  return response.data;
};

// Verify reset token is valid (before showing reset form)
export const verifyResetToken = async (token) => {
  const response = await API.get(`/auth/verify-reset-token/${token}`);
  return response.data;
};

// Reset password with token
export const resetPassword = async (token, newPassword) => {
  const response = await API.post('/auth/reset-password', {
    token,
    new_password: newPassword,
  });
  return response.data;
};

// ------------------ Email Verification ------------------

// Verify email with token
export const verifyEmail = async (token) => {
  return API.get(`/auth/verify-email/${token}`);
};

// Resend verification email
export const resendVerification = async (email) => {
  const response = await API.post('/auth/resend-verification', { email });
  return response.data;
};

// ------------------ MSAL helper exports ------------------

export const loginRedirect = () => msalInstance.loginRedirect(loginRequest);
export const loginPopup = () => msalInstance.loginPopup(loginRequest);
export const logout = () => {
  msalInstance.logoutRedirect();
};
export const getActiveAccount = () => {
  try {
    return msalInstance.getAllAccounts()[0] || null;
  } catch (error) {
    // MSAL not initialized yet
    return null;
  }
};

// ------------------ Credits API ------------------

// Get user's credit balance breakdown
export const getCreditBalance = async () => {
  const response = await API.get('/credits/balance');
  return response.data;
};

// Get available credits (minus pending authorizations)
export const getAvailableCredits = async () => {
  const response = await API.get('/credits/available');
  return response.data;
};

// Check if user has sufficient credits
export const checkCredits = async (creditsNeeded, operationType = 'resume_ranking') => {
  const response = await API.post('/credits/check', {
    credits_needed: creditsNeeded,
    operation_type: operationType,
  });
  return response.data;
};

// ------------------ Purchase policy & credit requests ------------------
// An org admin can switch off self-serve purchasing for their members. When they have,
// the member asks the admin for credits instead of buying. The backend enforces this on
// the purchase endpoints themselves — these calls only decide what UI to show.

// Resolve whether the current user may buy credits, and their open request if any.
export const getPurchasePolicy = async () => {
  const response = await API.get('/credits/purchase-policy');
  return response.data;
};

// Ask this org's admin for credits. Only valid when the policy says can_request.
export const createCreditRequest = async (creditsRequested, reason = '') => {
  const response = await API.post('/credits/requests', {
    credits_requested: creditsRequested,
    reason: reason || null,
  });
  return response.data;
};

// The current user's own credit requests, newest first.
export const getMyCreditRequests = async () => {
  const response = await API.get('/credits/requests/mine');
  return response.data;
};

// Withdraw one's own pending request, freeing the one-open-request slot.
export const cancelCreditRequest = async (requestId) => {
  const response = await API.post(`/credits/requests/${requestId}/cancel`);
  return response.data;
};

// Trigger a browser download from a blob response, honoring the server's
// Content-Disposition filename when present (shared by the CSV exporters below).
const triggerCsvDownload = (res, fallbackName) => {
  const cd = res.headers['content-disposition'] || res.headers['Content-Disposition'] || '';
  const match = /filename="?([^"]+)"?/.exec(cd);
  const filename = match ? match[1] : fallbackName;
  const blob = new Blob([res.data], { type: res.headers['content-type'] || 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// Get credit usage history
export const getCreditHistory = async (page = 1, pageSize = 20) => {
  const response = await API.get('/credits/history', {
    params: { page, page_size: pageSize },
  });
  return response.data;
};

// Export the full credit usage history (all entries) as a CSV download
export const exportCreditHistory = async () => {
  const res = await API.get('/credits/history/export', { responseType: 'blob' });
  triggerCsvDownload(res, 'credit_history.csv');
};

// Get available credit packages
export const getCreditPackages = async (includeBulk = false) => {
  const response = await API.get('/credits/packages', {
    params: { include_bulk: includeBulk },
  });
  return response.data;
};

// Get specific credit package
export const getCreditPackage = async (packageId) => {
  const response = await API.get(`/credits/packages/${packageId}`);
  return response.data;
};

// Get pending credit authorizations
export const getPendingAuthorizations = async () => {
  const response = await API.get('/credits/pending-authorizations');
  return response.data;
};

// Update low balance alert threshold
export const updateBalanceThreshold = async (threshold) => {
  const response = await API.put('/credits/balance/threshold', { threshold });
  return response.data;
};

// Get credit usage analytics
export const getCreditAnalytics = async (days = 30) => {
  const response = await API.get('/credits/analytics', {
    params: { days },
  });
  return response.data;
};

// Redeem a promo code for credits
export const redeemPromoCode = async (code) => {
  const response = await API.post('/credits/redeem', { code });
  return response.data;
};

// ------------------ Payments API ------------------

// Get available payment gateways
export const getPaymentGateways = async () => {
  const response = await API.get('/payments/gateways');
  return response.data;
};

// Create a payment order
export const createPaymentOrder = async (packageId, currency = 'INR', region = null) => {
  const response = await API.post('/payments/create-order', {
    package_id: packageId,
    currency,
    region,
  });
  return response.data;
};

// Verify payment completion
export const verifyPayment = async (transactionId, gatewayOrderId, gatewayPaymentId, signature = null) => {
  const response = await API.post('/payments/verify', {
    transaction_id: transactionId,
    gateway_order_id: gatewayOrderId,
    gateway_payment_id: gatewayPaymentId,
    signature,
  });
  return response.data;
};

// Verify Stripe payment completion
export const verifyStripePayment = async (transactionId, paymentIntentId) => {
  const response = await API.post('/payments/verify-stripe', {
    transaction_id: transactionId,
    payment_intent_id: paymentIntentId,
  });
  return response.data;
};

// Get payment transaction history
export const getPaymentTransactions = async (page = 1, pageSize = 20, status = null) => {
  const params = { page, page_size: pageSize };
  if (status) params.status = status;
  const response = await API.get('/payments/transactions', { params });
  return response.data;
};

// ------------------ Billing API ------------------

// Get billing history with invoices
export const getBillingHistory = async (page = 1, pageSize = 20, status = null) => {
  const params = { page, page_size: pageSize };
  if (status) params.status = status;
  const response = await API.get('/billing/history', { params });
  return response.data;
};

// Export the full billing ledger (all transactions) as a CSV download
export const exportBillingLedger = async (status = null) => {
  const params = status ? { status } : {};
  const res = await API.get('/billing/history/export', { responseType: 'blob', params });
  triggerCsvDownload(res, 'billing_ledger.csv');
};

// Download invoice PDF securely
export const downloadInvoice = async (invoiceId) => {
  const res = await API.get(`/billing/invoices/${invoiceId}/download`, { responseType: "blob" });
  const blob = new Blob([res.data], {
    type: res.headers["content-type"] || "application/pdf",
  });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `invoice_${invoiceId.slice(0, 8)}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// default export for direct axios usage if needed
export default API;
